/**
 * useInventory Hook - Wrapper for Inventory Store
 * 
 * Provides an interface for App.js to manage pantry items, scanning, and batch updates.
 */

import { useCallback } from 'react';
import { useInventoryStore } from '../stores/inventoryStore';
import { useUI } from './useUI';
import { InventoryUpdate } from '../types';
import { useAppContextStore } from '../stores/appContextStore';

export function useInventory(currentUser: any) {
    // Actions are stable in Zustand if retrieved via granular selectors
    const storeLoadInventory = useInventoryStore(state => state.loadInventory);
    const storeHandleAddItem = useInventoryStore(state => state.handleAddItem);
    const storeHandleDeleteItem = useInventoryStore(state => state.handleDeleteItem);
    const storeHandleScanInventory = useInventoryStore(state => state.handleScanInventory);
    const storeHandleManualParse = useInventoryStore(state => state.handleManualParse);
    const storeHandleStockKitchen = useInventoryStore(state => state.handleStockKitchen);
    const storeHandleAutoInventoryUpdate = useInventoryStore(state => state.handleAutoInventoryUpdate);
    const storeSetNewItem = useInventoryStore(state => state.setNewItem);
    const storeSetInventory = useInventoryStore(state => state.setInventory);
    const storeSetInventoryDraft = useInventoryStore(state => state.setInventoryDraft);
    const storeUpdateDraftQty = useInventoryStore(state => state.updateDraftQty);
    const storeRemoveFromDraft = useInventoryStore(state => state.removeFromDraft);

    // State subscription
    const inventory = useInventoryStore(state => state.inventory);
    const inventoryDraft = useInventoryStore(state => state.inventoryDraft);
    const newItem = useInventoryStore(state => state.newItem);
    const isAdding = useInventoryStore(state => state.isAdding);
    const updatedItems = useInventoryStore(state => state.updatedItems);
    const isLoading = useInventoryStore(state => state.isLoading);

    const { triggerToast, setFadingId, setActionLoading, setFadingInventoryItem } = useUI();

    const getToken = useCallback(async () => {
        if (!currentUser) return null;
        return await currentUser.getIdToken();
    }, [currentUser]);

    const loadInventory = useCallback(async () => {
        const token = await getToken();
        if (token) await storeLoadInventory(token);
    }, [getToken, storeLoadInventory]);

    const handleAddItem = useCallback(async () => {
        const token = await getToken();
        if (!token) return;

        try {
            const inventory = await storeHandleAddItem(token);
            triggerToast("Item added! 📦");
            // Surgical hydration instead of prefetch
            useAppContextStore.getState().hydrateStores({ inventory });
        } catch (err) {
            triggerToast("Failed to add item.");
        }
    }, [getToken, storeHandleAddItem, triggerToast]);

    const handleDeleteItem = useCallback(async (name: string) => {
        const token = await getToken();
        if (!token) return;

        setFadingInventoryItem(name);
        // Wait for the 300ms animation
        await new Promise(r => setTimeout(r, 300));

        setActionLoading(name);
        try {
            const inventory = await storeHandleDeleteItem(token, name);
            // Surgical hydration
            useAppContextStore.getState().hydrateStores({ inventory });
        } catch (err) {
            triggerToast(`Failed to delete ${name}`);
        } finally {
            setActionLoading(null);
            setFadingInventoryItem(null);
        }
    }, [getToken, storeHandleDeleteItem, setFadingInventoryItem, setActionLoading, triggerToast]);

    const processScan = useCallback(async (files: File[]) => {
        const token = await getToken();
        if (!token) return;

        try {
            await storeHandleScanInventory(token, files);
        } catch (err) {
            triggerToast("Scan failed.");
        }
    }, [getToken, storeHandleScanInventory, triggerToast]);

    const handleStockKitchen = useCallback(async () => {
        const token = await getToken();
        if (!token) return;

        // Fly animation trigger
        setFadingId('stocking');
        await new Promise(r => setTimeout(r, 800));

        try {
            const inventory = await storeHandleStockKitchen(token);
            if (inventory) {
                triggerToast("Kitchen stocked!");
                // Surgical hydration
                useAppContextStore.getState().hydrateStores({ inventory });
            }
            setFadingId(null);
        } catch (err) {
            setFadingId(null);
            triggerToast("Batch commitment failed.");
        }
    }, [getToken, storeHandleStockKitchen, setFadingId, triggerToast]);

    const handleAutoInventoryUpdate = useCallback(async (updates: InventoryUpdate) => {
        return storeHandleAutoInventoryUpdate(updates);
    }, [storeHandleAutoInventoryUpdate]);

    const handleManualParse = useCallback(async (text: string) => {
        const token = await getToken();
        if (token) await storeHandleManualParse(token, text);
    }, [getToken, storeHandleManualParse]);

    return {
        // State
        inventory,
        inventoryDraft,
        newItem,
        isAdding,
        updatedItems,
        isLoading,

        // Actions
        loadInventory,
        setNewItem: storeSetNewItem,
        setInventory: storeSetInventory,
        setInventoryDraft: storeSetInventoryDraft,
        handleAddItem,
        handleDeleteItem,
        processScan,
        handleStockKitchen,
        handleAutoInventoryUpdate,
        updateDraftQty: storeUpdateDraftQty,
        removeFromDraft: storeRemoveFromDraft,
        handleManualParse
    };
}
